import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthProvider } from '@providers/AuthProvider';
import { errorMessage, fieldAttributes } from '@helpers/Constants';
import Button from '@components/Button';
import { Axios, getErrorMessage } from '@helpers/ApiService';
import { useToast } from '@contexts/Toast';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { Logo } from '@components/Logo';
import { LoginForm } from '@helpers/Models';
import { NotificationManager } from '@services/NotificationManager';

export const Login: React.FC = () => {
  const { showToastMessage } = useToast();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<LoginForm>({
    email: '',
    password: '',
  });

  // field specific errors
  const [inputErrors, setInputErrors] = useState<{
    email?: string;
    password?: string;
  }>({});

  // checkAuth validation from auth provider
  useEffect(() => {
    const validateAuth = () => {
      if (AuthProvider.checkAuth()) {
        navigate(FrontendRoutes.DASHBOARD);
      }
    };

    validateAuth();
  }, [navigate]);

  /**
   * Handles the focus event on input fields.
   *
   * This function clears the general error message and the specific field error
   * for the input field that triggered the focus event.
   */
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setInputErrors((prev) => ({
      ...prev,
      // Clears specific field error
      [e.target.name]: undefined,
    }));
  };

  /**
   * Handles the form submission for the login form.
   */
  const handleSubmit: (
    e: React.FormEvent<HTMLFormElement>,
  ) => Promise<void> = async (e) => {
    e.preventDefault();
    const newErrors: { email?: string; password?: string } = {};

    if (!formData.email.trim()) {
      newErrors.email = errorMessage.FIELD_REQUIRED;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = errorMessage.INVALID_EMAIL;
    }

    if (!formData.password.trim()) {
      newErrors.password = errorMessage.FIELD_REQUIRED;
    } else if (formData.password.length < fieldAttributes.PASSWORD_MIN_LENGTH) {
      newErrors.password = errorMessage.INVALID_PASSWORD;
    }

    setInputErrors(newErrors);

    // stops form submission if there are field errors
    if (Object.keys(newErrors).length) {
      return;
    }

    try {
      const response = await Axios.post(ApiEndpoints.LOGIN, {
        email: formData.email,
        password: formData.password,
      });
      localStorage.setItem(
        'modules',
        JSON.stringify(response.data.data.modules),
      );

      // Set initial notifications using centralized NotificationManager
      if (
        response.data.data.notifications &&
        response.data.data.unreadNotifications
      ) {
        NotificationManager.setInitialNotifications(
          response.data.data.notifications,
          response.data.data.unreadNotifications,
        );
      }

      if (response.data.data.adminFlag) {
        localStorage.setItem('authToken', response.data.data.token);
        localStorage.setItem('adminFlag', '1');
        localStorage.setItem(
          'onboardingSteps',
          JSON.stringify(response.data.data.onBoardingSteps),
        );
        localStorage.setItem(
          'butcherDetails',
          JSON.stringify(response.data.data.butcherDetails),
        );

        localStorage.setItem(
          'userDetails',
          JSON.stringify({
            id: response.data.data.user.id,
            butcherId: response.data.data.user.butcherId,
            roles: response.data.data.user.roles,
            roleNames: response.data.data.user.roleNames,
          }),
        );

        // Store the subscription status in localStorage to control user access based on their subscription
        localStorage.setItem(
          'subscriptionStatus',
          JSON.stringify(response.data.data.subscriptionStatus),
        );
        // store the subscriptionActiveFlag status in localstorage to control the navigation
        localStorage.setItem(
          'subscriptionActiveFlag',
          JSON.stringify(response.data.data.subscriptionActiveFlag),
        );

        // If the admin's subscriptionActiveFlag is 0, redirect to the plan selection page
        // Otherwise, continue with the normal navigation flow
        if (response.data.data.subscriptionActiveFlag === 0) {
          navigate(FrontendRoutes.VIEW_PLANS);
          return;
        }

        //check for onboarding steps in the data and find the first incomplete step
        // if found, navigate to the onboarding step
        // else navigate to the customer list
        let hasIncompleteStep = false;

        // Safety check: ensure onBoardingSteps exists and is an array
        if (
          response.data.data.onBoardingSteps &&
          Array.isArray(response.data.data.onBoardingSteps)
        ) {
          for (const step of response.data.data.onBoardingSteps) {
            if (!step.isComplete) {
              navigate(step.route);
              hasIncompleteStep = true;
              break;
            }
          }
        }

        // Only navigate to the default route if no incomplete step was found
        if (!hasIncompleteStep) {
          if (
            response.data.data.routes &&
            response.data.data.routes.length > 0
          ) {
            navigate(response.data.data.routes[0].route);
          }
        }
      } else {
        localStorage.setItem('authToken', response.data.data.token);
        localStorage.setItem('adminFlag', '0');
        localStorage.setItem(
          'butcherDetails',
          JSON.stringify(response.data.data.butcherDetails),
        );

        localStorage.setItem(
          'userDetails',
          JSON.stringify({
            id: response.data.data.user.id,
            butcherId: response.data.data.user.butcherId,
            roles: response.data.data.user.roles,
            roleNames: response.data.data.user.roleNames,
          }),
        );
        // Store the subscription status in localStorage to control user access based on their subscription
        localStorage.setItem(
          'subscriptionStatus',
          JSON.stringify(response.data.data.subscriptionStatus),
        );
        // store the subscriptionActiveFlag status in localstorage to control the navigation
        localStorage.setItem(
          'subscriptionActiveFlag',
          JSON.stringify(response.data.data.subscriptionActiveFlag),
        );

        // Navigate to Profile Details for Non Admin users if the
        // Butcher Admin doesn't have a active plan
        if (response.data.data.subscriptionActiveFlag == 0) {
          navigate(FrontendRoutes.PROFILE_DETAILS);
        } else {
          if (response.data.data.routes) {
            localStorage.setItem(
              'routes',
              JSON.stringify(response.data.data.routes),
            );
            navigate(response.data.data.routes[0].route);
          }
        }
      }
    } catch (error) {
      showToastMessage(getErrorMessage(error), 'error');
    }
  };

  // handler for updating formData state for changed input field
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8">
      {/* Logo image import */}
      <div className="w-full max-w-md space-y-8">
        <div className="flex justify-center mb-8">
          <Logo isLogin={true} />
        </div>

        <div className="mt-8">
          <h2 className="font-bold tracking-tight text-2xl text-center text-gray-900">
            Log in to your account
          </h2>
        </div>

        {/* Input fields */}
        <form className="mt-8 space-y-6" noValidate onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                E-Mail *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                className={`mt-1 block w-full rounded-md border px-3 py-2 ${inputErrors.email ? 'border-red-500' : 'outline-gray-300 focus:outline-primary'}`}
                value={formData.email}
                onChange={handleInputChange}
                onFocus={handleFocus}
              />
              {inputErrors.email && (
                <p className="mt-1 text-sm text-red-500">{inputErrors.email}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password *
              </label>
              <input
                id="password"
                name="password"
                type="password"
                className={`mt-1 block w-full rounded-md border px-3 py-2 ${inputErrors.password ? 'border-red-500' : 'outline-gray-300 focus:outline-primary'}`}
                value={formData.password}
                onChange={handleInputChange}
                onFocus={handleFocus}
                title="Password must be at least 8 characters long"
              />
              {inputErrors.password && (
                <p className="mt-1 text-sm text-red-500">
                  {inputErrors.password}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="rememberMe"
                type="checkbox"
                className="h-4 w-4 shrink-0 accent-primary focus:ring-primary border-gray-300 rounded"
                onChange={handleInputChange}
                onFocus={handleFocus}
              />
              <label
                htmlFor="remember-me"
                className="ml-2 block text-sm text-gray-900"
              >
                Remember Me
              </label>
            </div>
          </div>

          <div className="text-sm">
            <Link
              to="/forgot-password"
              className="flex justify-center items-center h-full font-medium text-primary hover:underline"
            >
              Forgot Password?
            </Link>
          </div>

          {/* Login button */}
          <div>
            <Button name="Login" />
          </div>

          <div className="text-center text-sm">
            {"Don't have an account? "}
            <button
              type="button"
              className="font-medium text-primary hover:underline"
              onClick={() => navigate(FrontendRoutes.REGISTRATION)}
            >
              Sign up
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
